import { FleetManagementStatsOutput } from '@allocab/allocab-driver-shared-types';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import { NetGainsWidget } from './_authenticated._main.invoicing.invoices/NetGainsWidget';
import { formatTimeScopeToInput } from './_authenticated._main.invoicing.invoices/utils';
import { Link } from '../components/atomic/Link';
import { Table } from '../components/table/Table';
import { TableColumn } from '../components/table/types';
import { getDefaultTimeScopeValues } from '../components/time-scope-picker/utils';
import { SCOPE_DATA_SEARCH_PARAM_KEY, ScopeData, TimeScopePagination } from '../components/TimeScopePagination';
import { DRIVER_NET_GAIN_LIMIT, useFetchNetGainsByDriverPage } from '../hooks/useFetchNetGainsByDriver';
import { decodeObject } from '../hooks/useJSONSearchParam';
import { t } from '../services/translation/translation';
import { formatPrice } from '../utils/price';

const minDate = new Date(2025, 0, 1).valueOf();

const columns: TableColumn<FleetManagementStatsOutput>[] = [
  {
    name: t('common.name'),
    render: item => <Link to={`/drivers/${item.driverKey}`}>{item.driverName}</Link>,
  },
  {
    name: t('common.amount'),
    render: item => <div>{formatPrice(item.netGain.netGain.amount)}</div>,
  },
  {
    name: t('common.onboard'),
    render: item => <div>{formatPrice(item.netGain.netGain.amountPaidOnBoard)}</div>,
  },
  {
    name: t('common.nbHoursDone'),
    render: item => <div>{item.performance.nbHoursDone}</div>,
  },
  {
    name: t('common.nbKmsDone'),
    render: item => <div>{item.performance.nbKmsDone}</div>,
  },
  {
    name: t('common.nbTripsDone'),
    render: item => <div>{item.performance.nbTripsDone}</div>,
  },
];

export const FleetRoute = () => {
  const [searchParams] = useSearchParams();
  const maxDate = useMemo(() => Date.now(), []);
  const defaultTimeScope = useMemo(() => getDefaultTimeScopeValues(), []);
  const scopeData = decodeObject<ScopeData>(searchParams.get(SCOPE_DATA_SEARCH_PARAM_KEY)) ?? {
    scope: defaultTimeScope,
    mode: 'fortnight',
  };

  const { data, isLoading, isFetching, isPreviousData } = useFetchNetGainsByDriverPage(
    formatTimeScopeToInput(scopeData.scope, scopeData.mode),
  );

  const nbPages = Math.ceil((data?.totalItems ?? 0) / DRIVER_NET_GAIN_LIMIT);

  return (
    <div>
      <Helmet>
        <title>{t('fleet-route.title')} - Allocab</title>
      </Helmet>

      <h3 className="text-3xl font-bold dark:text-white mb-6">{t('fleet-route.title')}</h3>

      <div>
        <TimeScopePagination minDate={minDate} maxDate={maxDate} defaultTimeScope={defaultTimeScope} />

        <div className="py-2">
          <NetGainsWidget mode={scopeData.mode} scope={scopeData.scope} />
        </div>

        <Table
          identifier="driverKey"
          columns={columns}
          data={data?.items}
          isLoading={isLoading}
          isFetching={isPreviousData && isFetching}
          nbPages={nbPages}
          context={undefined}
          totalItems={data?.totalItems ?? 0}
        />
      </div>
    </div>
  );
};
